import * as React from 'react';
import classnames from 'classnames';
import { CoreCheckbox } from '../internal/WixUiCoreComponents/components/CoreCheckbox';
import {
  CheckXBold,
  CheckXMedium,
  CheckXSmall,
  IndeterminateXSmall,
} from '@wix/wix-ui-icons-common/on-stage/system';
import { CHECKBOX_DATA_HOOKS, CHEKCBOX_DATA_KEYS } from './dataHooks';
import { st, classes } from './Checkbox.st.css';
import { IconTooltip } from '../IconTooltip';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { TooltipSkin } from '../Tooltip/types';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import {
  CheckboxVerticalAlignment,
  CheckboxDefaultProps,
  CheckboxProps,
  CheckboxState,
  CheckboxTheme,
  CheckmarkStyle,
} from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import uniqueId from '../../common/uniqueId';
import {
  TPAComponentsConfig,
  TPAComponentsContext,
} from '../TPAComponentsConfig';
import { Asterisk } from '../internal/Asterisk/Asterisk';

/** An implementation of Checkbox for TPAs */
export class Checkbox extends React.Component<CheckboxProps, CheckboxState> {
  static displayName = 'Checkbox';
  private readonly inputRef = React.createRef<CoreCheckbox>();
  private readonly _rootRef = React.createRef<HTMLDivElement>();
  private readonly _errorIconRef = React.createRef<HTMLSpanElement>();
  private readonly _errorMessageId: string;
  private readonly _errorIconId: string;
  private readonly _checkboxId: string;
  static defaultProps: CheckboxDefaultProps = {
    checked: false,
    disabled: false,
    label: '',
    error: false,
    errorMessage: '',
    indeterminate: false,
    theme: CheckboxTheme.Default,
    'data-hook': CHECKBOX_DATA_HOOKS.CheckboxWrapper,
  };
  static contextType = TPAComponentsContext;

  state: CheckboxState = {
    focused: false,
    isErrorTooltipShown: false,
    errorIconClicked: false,
    isWithinRoot: false,
  };

  constructor(props: CheckboxProps) {
    super(props);
    this._errorMessageId = uniqueId('error-message');
    this._errorIconId = uniqueId('error-icon');
    this._checkboxId = uniqueId('checkbox');
  }

  getDataAttributes() {
    const { disabled, error, indeterminate, checked } = this.props;
    const { focused } = this.state;

    return {
      [CHEKCBOX_DATA_KEYS.Disabled]: disabled,
      [CHEKCBOX_DATA_KEYS.Error]: error,
      [CHEKCBOX_DATA_KEYS.Indeterminate]: indeterminate,
      [CHEKCBOX_DATA_KEYS.Checked]: checked,
      [CHEKCBOX_DATA_KEYS.Focused]: focused,
    };
  }

  public focus = (options?: FocusOptions) => {
    if (this.inputRef.current) {
      this.inputRef.current.focus(options);
      this.setState({ focused: true });
    }
  };

  public showErrorTooltip = () => {
    this._toggleTooltip(true);
  };

  public hideErrorTooltip = () => {
    this._toggleTooltip(false);
  };

  _renderIcon = () => {
    const { checked, indeterminate } = this.props;

    const CheckmarkIcon = this.getIcon();

    return (
      <span
        className={classnames(classes.icon)}
        data-hook={CHECKBOX_DATA_HOOKS.IconWrapper}
      >
        {checked ? (
          <CheckmarkIcon />
        ) : indeterminate ? (
          <IndeterminateXSmall />
        ) : (
          ''
        )}
      </span>
    );
  };

  _onRootFocus = () => {
    const { isWithinRoot, errorIconClicked } = this.state;

    if (!isWithinRoot && !errorIconClicked) {
      this.setState({ isWithinRoot: true });
      this._toggleTooltip(true);
    }
  };

  _onRootKeyDown = (e) => {
    const { id: activeElementId } = document.activeElement || {};

    if (
      document.activeElement &&
      ((activeElementId === this._errorIconId && this._wasTabClicked(e)) ||
        (activeElementId === this._checkboxId && this._wasShiftTabClicked(e)))
    ) {
      // The ErrorIcon / Checkbox input are at the edge of the Component - Tab click / Shift Tab Click exits the Component accordingly
      this._toggleTooltip(false);
      this.setState({ isWithinRoot: false });
    }
  };

  _wasShiftTabClicked = (e) => {
    return e.shiftKey && e.key === 'Tab';
  };

  _wasTabClicked = (e) => {
    return !e.shiftKey && e.key === 'Tab';
  };

  _onFocus = () => {
    this.setState({ focused: true });
  };

  _onBlur = () => {
    this.setState({ focused: false });
  };

  _toggleTooltip = (showTooltip) => {
    this.setState({ isErrorTooltipShown: showTooltip });
  };

  _onErrorIconMouseDown = () => {
    this.setState({ errorIconClicked: true });
  };

  _onErrorIconClick = () => {
    const { isErrorTooltipShown } = this.state;

    this._toggleTooltip(!isErrorTooltipShown);
    this.setState({ errorIconClicked: false });
  };

  _onClickOutside = () => {
    if (
      this._rootRef.current &&
      !this._rootRef.current.contains(document.activeElement)
    ) {
      this._toggleTooltip(false);
      this.setState({ isWithinRoot: false });
    }
  };

  _getErrorSuffix = () => {
    const { isErrorTooltipShown } = this.state;
    const { errorMessage } = this.props;

    return (
      <div className={classes.iconToolTip}>
        <IconTooltip
          data-hook={CHECKBOX_DATA_HOOKS.ErrorIcon}
          message={errorMessage}
          icon={<Error className={classes.errorIcon} />}
          tooltipId={this._errorMessageId}
          tooltipSkin={TooltipSkin.Error}
          showTooltip={isErrorTooltipShown}
          onMouseDown={this._onErrorIconMouseDown}
          onClick={this._onErrorIconClick}
          onClickOutside={this._onClickOutside}
          contentRef={this._errorIconRef}
          id={this._errorIconId}
        />
      </div>
    );
  };

  getIcon = () => {
    const { checkmarkStyle } = this.props;
    if (checkmarkStyle === CheckmarkStyle.Bold) {
      return CheckXBold;
    }
    if (checkmarkStyle === CheckmarkStyle.Regular) {
      return CheckXSmall;
    }
    return CheckXMedium;
  };

  render() {
    const {
      theme,
      checked,
      disabled,
      label,
      error,
      errorAppearance,
      errorMessage,
      newErrorMessage,
      indeterminate,
      onChange,
      name,
      suffix,
      className,
      'aria-describedby': ariaDescribedByProp,
      'aria-label': ariaLabel,
      required,
      showAsterisk = true,
      verticalAlignment = CheckboxVerticalAlignment.Center,
      lang,
    } = this.props;
    const { mobile: isMobile } = this.context as TPAComponentsConfig;

    const iconContent = this._renderIcon();
    const shouldShowErrorIcon = error && errorMessage && !newErrorMessage;
    const Wrapper = newErrorMessage ? ErrorMessageWrapper : React.Fragment;
    const box = theme === 'box';
    const stStates = {
      error,
      newErrorMessage,
      box,
      disabled,
      checked: !!checked,
    };
    const isErrorMessageProvided = errorMessage && errorMessage.length > 0;
    const renderRequiredAsterisk = required && showAsterisk;
    const originalClassNameValue = st(
      classes.root,
      { disabled, error, newErrorMessage, checked: !!checked, box },
      className,
    );
    const newErrorMessageClassNameValue = classes.container;
    const containerClassNameValue = !newErrorMessage
      ? originalClassNameValue
      : newErrorMessageClassNameValue;

    const ariaDescribedBy = getAriaDescribedBy([
      ariaDescribedByProp,
      errorMessage ? this._errorMessageId : '',
    ]);

    return (
      <Wrapper
        {...(newErrorMessage && {
          visible: error && isErrorMessageProvided,
          message: errorMessage,
          messageId: this._errorMessageId,
          className: st(classes.root, stStates, className),
          'data-hook': this.props['data-hook'],
          errorAppearance,
          lang,
        })}
      >
        <div
          className={st(
            containerClassNameValue,
            !isMobile && this.state.focused && classes.focused,
          )}
          data-hook={this.props['data-hook']}
          ref={this._rootRef}
          onFocus={this._onRootFocus}
          onKeyDown={this._onRootKeyDown}
          {...this.getDataAttributes()}
          {...(!newErrorMessage ? { lang } : {})}
        >
          <CoreCheckbox
            className={st(classes.core, { verticalAlignment })}
            checkedIcon={iconContent}
            uncheckedIcon={iconContent}
            indeterminateIcon={iconContent}
            indeterminate={indeterminate}
            checked={checked}
            onChange={onChange}
            name={name}
            disabled={disabled}
            ref={this.inputRef}
            onFocusByKeyboard={this._onFocus}
            onBlur={this._onBlur}
            data-hook={CHECKBOX_DATA_HOOKS.CheckboxCore}
            aria-invalid={error}
            aria-describedby={ariaDescribedBy}
            aria-label={ariaLabel}
            id={this._checkboxId}
            required={required}
          >
            <div
              data-hook={CHECKBOX_DATA_HOOKS.LabelWrapper}
              className={classnames(classes.label, {
                [classes.suffixed]: !!suffix,
              })}
            >
              {label}
              {renderRequiredAsterisk && <Asterisk />}
            </div>
            {suffix && (
              <div className={`${classes.label} ${classes.suffix}`}>
                {suffix}
              </div>
            )}
            {shouldShowErrorIcon ? this._getErrorSuffix() : null}
          </CoreCheckbox>
        </div>
      </Wrapper>
    );
  }
}
